import React, { useCallback, useMemo, useState } from 'react'

import { breakpoints, useDimensions } from 'shared/dimensions'
import { ModalWindow } from 'shared/modal-window'
import { ContactUsModal, StatusModal } from 'app/features/modals'
import { PROJECTS } from 'app/data/projects'
import { SliderMobile } from 'app/features/slider/organisms/slider-mobile'
import { OutsourceOutstaffPopup } from 'app/features/modals/organisms/outsource-outstaff'
import { ProjectSlidePopupMobile } from 'app/features/slider/molecules/project-slide-popup-mobile'

import { Icon, MobileUpperFrame, Text } from 'ui/components'
import { styled, theme } from 'ui/styles'
import { connect } from 'react-redux'
import { RootState } from 'root-reducer'

import { close, setActiveSection, setIsOpen } from 'store/project/popup/actions'
import { setStatusModalVisible } from 'store/status/actions'
import { paths } from 'shared/routing'
import { Link, useLocation } from 'react-router-dom'
import { TContact } from './contacts'
import { ContactRow } from './contact-row'

const PROJECTS_LINKS = [
  {
    name: 'Заказная разработка',
    path: paths.development,
  },
  {
    name: 'Выделенные проектные команды',
    path: paths.team,
  },
  {
    name: 'Языки программирования и инструменты разработки',
    path: paths.languages,
  },
  {
    name: 'Стоимость',
    path: paths.price,
  },
];

const CONTACTS_DATA: TContact[] = [
  {
    data: [
      'Общество с ограниченной ответственностью',
      '"АРГО СТУДИО"',
      'ИНН 7604238866',
      'КПП 760401001',
      'ОГРН 1137604002304',
      'Код ОКВЭД 62.01',
      'ПЕРЕЧЕНЬ ВИДОВ ДЕЯТЕЛЬНОСТИ В ОБЛАСТИ ИНФОРМАЦИОННЫХ ТЕХНОЛОГИЙ 1.01  1.02  1.04  1.05'
    ]
  },
  {
    data: [
      'Адрес: г. Ярославль,',
      'ул. Свободы, д. 71А, помещ. 1-20, этаж 4'
    ]
  }
]

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 48px;
  height: 100%;
  justify-content: space-between;
`

const SectionLink = styled(Link)<{ isActive?: boolean }>`
  display: flex;
  align-items: start;
  gap: 8px;
  text-align: left;
  transition: color ${theme.transition.hover}ms linear;
  color: ${({ isActive }) => (isActive ? theme.palette.dark_blue : theme.palette.pink_red)};
  margin-top: 20px;

  hyphens: none;
  -webkit-hyphens: none;
  -ms-hyphens: none;

  @media (max-width: ${breakpoints.tablet - 1}px) {
    hyphens: manual;
    -webkit-hyphens: manual;
    -ms-hyphens: manual;
  }

  * svg * {
    stroke: ${({ isActive }) => (isActive ? theme.palette.dark_blue : theme.palette.pink_red)};
    transition: stroke ${theme.transition.hover}ms linear;
  }

  &:hover {
    color: ${theme.palette.dark_blue};
    * svg * {
      stroke: ${theme.palette.dark_blue};
    }
  }
`

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 24px;
`

const StyledText = styled(Text)`
  margin-bottom: 12px;
`

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
`

const ContactsList = styled.div`
  display: flex;
  flex-direction: column;
`

export const ProjectsContainer: React.FC<ContainerProps> = ({
  isPopupOpen,
  activeSection,
  activeStatus,
  setStatusVisible,
  closePopup
}) => {
  const location = useLocation()

  const [contactUsModalVisible, setContactUsModalVisible] = React.useState(false)

  const handleOverlayClick = () => {
    setContactUsModalVisible(false)
    setStatusVisible(false)
  }

  const handleSubmit = () => {
    setContactUsModalVisible(false)
    setStatusVisible(true)
  }

  const [isOpenMobileSlide, setIsOpenMobileSlide] = useState(false)
  const [currentSlide, setCurrentSlide] = useState<typeof PROJECTS[number]>(PROJECTS[0])

  const { type } = useDimensions()
  const isMobile = type === 'mobile'

  const jsxSliderMobile = useMemo(
    () =>
      isMobile && location.pathname === paths.projects && (
        <SliderMobile
          data={PROJECTS.map((el) => ({
            image: el.portfolio,
            title: el.title,
            chips: el.chips,
            onClick: () => {
              setCurrentSlide(el)
              setIsOpenMobileSlide(true)
            }
          }))}
        />
      ),
    [isMobile, location.pathname]
  )

  const closeSlidePopupMobile = useCallback(() => setIsOpenMobileSlide(false), [])

  return (
    <Wrapper>
      <MobileUpperFrame openingParameter={isOpenMobileSlide}>
        <ProjectSlidePopupMobile data={currentSlide} onClose={closeSlidePopupMobile} />
      </MobileUpperFrame>

      <MobileUpperFrame openingParameter={isPopupOpen}>
        <OutsourceOutstaffPopup closePopup={closePopup} activeSection={activeSection} />
      </MobileUpperFrame>

      <div>
        <InfoWrapper>
          <StyledText variant={'h3'}>
            Более 8 лет разрабатываем
            <br />
            ИТ-продукты для бизнеса
          </StyledText>
          {PROJECTS_LINKS.map((el) => (
            <SectionLink
              key={el.path}
              to={el.path}
              isActive={location.pathname === el.path}
              target={isMobile ? '_blank' : undefined}
              rel={isMobile ? 'noopener noreferrer' : undefined}
            >
              <Icon name="arrows/right" paddingTop={6} />
              {el.path === paths.languages ? (
                <Text variant="h4" lang="ru">
                  Языки програм&shy;мирования и инструменты разработки
                </Text>
              ) : (
                <Text variant="h4">{el.name}</Text>
              )}
            </SectionLink>
          ))}
        </InfoWrapper>

        <ContactsList>
          <ContactRow data={CONTACTS_DATA[0].data} padding={'0 0 0 32px'} />
        </ContactsList>

        <ContactsList>
          <ContactRow data={CONTACTS_DATA[1].data} padding={'0 0 0 32px'} />
        </ContactsList>
      </div>

      <BottomSection>
        {jsxSliderMobile}
      </BottomSection>

      <ModalWindow
        isVisible={contactUsModalVisible || activeStatus}
        onOverlayClick={handleOverlayClick}
        contentMaxWidth="clamp(0px, 95vw, 704px)"
      >
        {activeStatus && <StatusModal onClose={() => setStatusVisible(false)} isSuccess={true} />}
        {contactUsModalVisible && (
          <ContactUsModal onClose={() => setContactUsModalVisible(false)} onSubmit={handleSubmit} />
        )}
      </ModalWindow>
    </Wrapper>
  )
}

const mapStateToProps = (state: RootState) => ({
  isPopupOpen: state.popup.isOpen,
  activeSection: state.popup.activeSection,
  activeStatus: state.statusModal.activeStatusModal
})

const mapDispatchToProps = (dispatch: any) => ({
  setIsPopupOpen: (isOpen: boolean) => dispatch(setIsOpen(isOpen)),
  setActiveProjectSection: (activeSection: number) => dispatch(setActiveSection(activeSection)),
  setStatusVisible: (status: boolean) => dispatch(setStatusModalVisible(status)),
  closePopup: () => dispatch(close())
})

export type ContainerProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>

export const Projects = connect(mapStateToProps, mapDispatchToProps)(ProjectsContainer)
